// Print an 80mm monospace KOT for ONE dine-in round. A KOT is a kitchen docket, so it carries NO
// prices — only what to cook: quantity, item, variant/add-on labels and the per-item note. Cancelled
// lines are struck through under a CANCELLED heading with their reason, so the line never prints
// invisibly. A reprint is stamped ** REPRINT **. (G-8)
//
// The docket goes out through the shared print helper (lib/print.js) — a hidden iframe, never a
// pop-up window, because a tablet silently blocks a pop-up opened after an await and the kitchen
// would never learn the docket was lost.

import { printHtml, pagePrintCss } from "./print";

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function lineDetail(it) {
  return [it.variant_label, ...((it.addons || []).map((a) => a && a.name))].filter(Boolean).join(" · ");
}

function rowHtml(it, struck) {
  const details = lineDetail(it);
  const strike = struck ? "text-decoration:line-through" : "";
  // Item-code prefix, same "[159] Name" format the takeaway docket uses (components/tabon/Orders.jsx)
  // so Vinod's code-organised menu reads the same on both dockets. Degrades to just the name when an
  // item has no code — mixed rows are normal (AF-5: ~49 of 178 items have none).
  const code = it.item_code ? `[${esc(it.item_code)}] ` : "";
  return `<tr>
      <td style="padding:3px 4px;font-size:15px;font-weight:bold;vertical-align:top;${strike}">${esc(it.qty)}x</td>
      <td style="padding:3px 4px;font-size:15px;width:100%">
        <span style="font-weight:bold;${strike}">${code}${esc(it.name)}</span>
        ${details ? `<br><span style="font-size:12px;font-weight:bold">${esc(details)}</span>` : ""}
        ${it.note ? `<br><span style="font-size:12px">» ${esc(it.note)}</span>` : ""}
        ${struck && it.cancelled_reason ? `<br><span style="font-size:12px">${esc(it.cancelled_reason)}</span>` : ""}
      </td>
    </tr>`;
}

// round-shaped input: { kotNumber, placedAt, items:[{qty,name,variant_label,addons,note,cancelled,cancelled_reason}],
//                       venueName, location, reprint, widthMm }
// widthMm is the venue's thermal roll width (80 default, 58 for small Bluetooth units).
export function printRoundKOT({ kotNumber, placedAt, items, venueName, location, reprint, widthMm = 80, refType, refId }) {
  const w = widthMm === 58 ? 58 : 80;
  const list = items || [];
  const active = list.filter((it) => !it.cancelled);
  const cancelled = list.filter((it) => it.cancelled);
  const time = placedAt
    ? new Date(placedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "";
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>KOT ${esc(kotNumber)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: ${w}mm; padding: 8px; color: #000; }
        .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 6px; margin-bottom: 6px; }
        .venue { font-size: 14px; font-weight: bold; }
        .kot-num { font-size: 20px; font-weight: bold; margin: 4px 0; }
        .meta { font-size: 12px; }
        .reprint { font-size: 14px; font-weight: bold; margin-top: 4px; letter-spacing: 1px; }
        table { width: 100%; border-collapse: collapse; margin: 4px 0; }
        .divider { border-top: 1px dashed #000; margin: 6px 0; }
        .cancel-head { font-size: 13px; font-weight: bold; text-align: center; letter-spacing: 1px; }
        .footer { text-align: center; font-size: 10px; margin-top: 8px; color: #666; }
        ${pagePrintCss(w)}
      </style>
    </head>
    <body>
      <div class="header">
        <div class="venue">${esc(venueName)}</div>
        <div class="kot-num">KOT ${esc(kotNumber)}</div>
        <div class="meta">${esc(location || "")}${time ? ` &nbsp;|&nbsp; ${time}` : ""}</div>
        ${reprint ? `<div class="reprint">** REPRINT **</div>` : ""}
      </div>
      <table>${active.map((it) => rowHtml(it, false)).join("")}</table>
      ${cancelled.length ? `
        <div class="divider"></div>
        <div class="cancel-head">CANCELLED</div>
        <table>${cancelled.map((it) => rowHtml(it, true)).join("")}</table>
      ` : ""}
      <div class="footer">Powered by TabOn · tabon.app</div>
    </body>
    </html>
  `;
  return printHtml(html, "kitchen docket", { document: "kot", refType, refId });
}
